import { Alert, AlertTitle, Box, Stack, useTheme } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import type { WorkflowDiagramStep } from "../../../../../api/types/workflowTypes";
import { computeConnectorPaths } from "./workflowDiagramHelper";
import type { ConnectorPath, WorkflowGraph } from "./workflowDiagramTypes";
import WorkflowEdges from "./WorkflowEdges";
import WorkflowStepCard from "./WorkflowStepCard";

interface Props {
  stepsMap: Record<string, WorkflowDiagramStep>;
  workflowGraph: WorkflowGraph;
  isCurrentTemplateVersion: boolean;
}

const WorkflowDiagram = ({ stepsMap, workflowGraph, isCurrentTemplateVersion }: Props) => {
  const theme = useTheme();

  const nodeElementsRef = useRef<Record<string, HTMLDivElement>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const [connectorPaths, setConnectorPaths] = useState<ConnectorPath[]>([]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const paths = computeConnectorPaths(
        workflowGraph.edges,
        nodeElementsRef.current,
        containerRef.current.getBoundingClientRect()
      );

      setConnectorPaths(paths);
    }
  }, [workflowGraph.edges]);

  return (
    <>
      {!isCurrentTemplateVersion && (
        <Alert severity="warning" sx={{ mb: 3, maxWidth: "35rem" }}>
          <AlertTitle>Outdated workflow template version</AlertTitle>
          This is not the current version of the workflow template. Editing is disabled.
        </Alert>
      )}
      <Box ref={containerRef} sx={{ position: "relative" }}>
        <Stack direction="row" spacing={6} sx={{ alignItems: "start" }}>
          {workflowGraph.stepIdsByIndex.map((stepsIds, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Stack key={index} spacing={6} sx={{ alignItems: "flex-end" }}>
              {stepsIds.map((stepId) => {
                const step = stepsMap[stepId];
                return step ? (
                  <WorkflowStepCard
                    key={stepId}
                    step={step}
                    headerRef={(ref: HTMLDivElement) => {
                      nodeElementsRef.current[stepId] = ref;
                    }}
                  />
                ) : null;
              })}
            </Stack>
          ))}
        </Stack>

        <WorkflowEdges connectorPaths={connectorPaths} color={theme.palette.divider} horizontalGap={24} />
      </Box>
    </>
  );
};

export default WorkflowDiagram;
